import times from "lodash/times"
import { addDays, differenceInDays } from "date-fns"
import { getUTCNoon } from "@mallardbay/lib-shared-helpers"

import {
    BookingInternalSource,
    BookingInternalSourceEntityType,
    type AddPackageToQuoteInput,
    type CreateQuoteInput,
    type MutationCreateQuoteArgs,
    type QuoteAddOnInput,
} from "~graphql/generated/graphql"
import type { AddOnInput } from "~components/pages/listing/checkout/checkout-flow/checkout-flow-types"
import { useWidgetContext } from "~widget/components/widget-provider"

import { getInternalSourceQueryParams } from "./url-param-helpers"

export interface UseDraftQuoteArgs {
    pricingPackageId?: string
    dates: Date[]
    addOns: AddOnInput[]
    totalGuests: number
    coupon?: string
    bookingInviteId?: string
    listingId?: string
}

export interface UseCreateQuoteArgs {
    dates: Date[]
    packages: QuotePackage[]
    coupon?: string
    bookingInviteId?: string
    listingId?: string
}

export interface QuotePackage extends AddPackageToQuoteInput {
    add_ons: AddOnInput[]
}

export function useCreateQuoteParams({
    dates,
    coupon,
    bookingInviteId,
    listingId,
    packages,
}: UseCreateQuoteArgs): MutationCreateQuoteArgs {
    const adjustedDates = adjustDatesForBackend({
        dates,
    })

    const formattedPackages = packages.map((quotePackage) => {
        const adjustedAddOns = getAddOnInputsForBackend(quotePackage.add_ons)

        return { ...quotePackage, add_ons: adjustedAddOns }
    })
    const attribution = useAttributionProps({ listingId })
    return {
        data: {
            dates: adjustedDates,
            packages: formattedPackages,
            coupon,
            booking_invite: bookingInviteId,
            ...attribution,
        },
    }
}

export function useDraftQuoteParams({
    pricingPackageId,
    dates,
    addOns,
    totalGuests,
    coupon,
    bookingInviteId,
    listingId,
}: UseDraftQuoteArgs): {
    data: CreateQuoteInput
} {
    const adjustedDates = adjustDatesForBackend({
        dates,
    })

    const adjustedAddOns = getAddOnInputsForBackend(addOns)
    const attribution = useAttributionProps({ listingId })
    return {
        data: {
            dates: adjustedDates,
            coupon,
            booking_invite: bookingInviteId,
            packages: [
                {
                    // Query is skipped if there's no id.
                    pricing_package_id: pricingPackageId ?? "",
                    add_ons: adjustedAddOns,
                    number_of_guests: totalGuests,
                },
            ],
            ...attribution,
        },
    }
}

export function adjustDatesForBackend({ dates }: { dates: Date[] }) {
    const [startDate, endDate] = getTripDates(dates)
    if (!startDate || !endDate) return []

    const numberOfDays = getNumberOfDaysFromStartAndEndDates({
        startDate,
        endDate,
    })

    return times(numberOfDays, (idx) => addDays(startDate, idx).getTime())
}

export function getAddOnInputsForBackend(
    addOns?: AddOnInput[]
): QuoteAddOnInput[] {
    if (!addOns) return []

    return addOns.map((addOn) => ({ id: addOn.id, quantity: addOn.quantity }))
}

function getTripDates(dates: Date[]) {
    // If only one date is selected, set it as the start/end date.
    // Using spread to maintain the types.
    const adjustedDates = dates.length === 1 ? [...dates, ...dates] : dates

    return adjustedDates.map((date) => getUTCNoon(date))
}

function getNumberOfDaysFromStartAndEndDates({
    startDate,
    endDate,
}: {
    startDate: Date
    endDate: Date
}) {
    return differenceInDays(endDate.setHours(12), startDate.setHours(12)) + 1
}

function useAttributionProps({ listingId }: { listingId?: string }) {
    const { isWidgetCheckout, widget } = useWidgetContext()

    const defaultSource = isWidgetCheckout
        ? BookingInternalSource.GuideTech
        : undefined
    const defaultEntityId = isWidgetCheckout ? widget?.id : listingId
    const defaultEntityType = isWidgetCheckout
        ? BookingInternalSourceEntityType.Widgets
        : BookingInternalSourceEntityType.Listings

    return getInternalSourceQueryParams({
        defaultSource,
        defaultEntityId,
        defaultEntityType,
    })
}
